/**
 * Display names for the Notfallversorgung (emergency-care) enum tokens, shared
 * by the clinic hero band and the "Notfallversorgung" content section.
 *
 * Two endpoints carry the same two values in different shapes and casings:
 * `GET /clinics/{clinicSn}/metadata` nests them camelCase under
 * `notfallversorgung` (BDA-5824), while `GET /clinics/{clinicSn}/emergency`
 * returns them snake_case alongside the reporting `year`. Only the *labels* are
 * shared here — the two response shapes stay separate types.
 *
 * The tokens are inlined rather than fetched: they are a closed, rarely-changing
 * set and the hero renders above the fold on a single `/metadata` request, so
 * resolving one label must not cost another round trip.
 *
 * `short` is the compact form for chips and table cells, `full` the wording for
 * a tooltip or a detail field.
 */
export interface AysClinicNotfallversorgungLabel {
  short: string;
  full: string;
}

/** The two Notfallversorgung values as `/metadata` nests them (camelCase). */
export interface AysClinicNotfallversorgung {
  participationState?: string;
  level?: string;
}

/** Notfallstufe tokens. Keys are lower-cased for case-insensitive matching. */
export const AYS_CLINIC_NOTFALL_LEVEL_DISPLAY_NAMES: Record<
  string,
  AysClinicNotfallversorgungLabel
> = {
  basisnotfallversorgung_stufe_1: {
    short: 'Notfallstufe 1',
    full: 'Basisnotfallversorgung (Stufe 1)',
  },
  erweiterte_notfallversorgung_stufe_2: {
    short: 'Notfallstufe 2',
    full: 'Erweiterte Notfallversorgung (Stufe 2)',
  },
  umfassende_notfallversorgung_stufe_3: {
    short: 'Notfallstufe 3',
    full: 'Umfassende Notfallversorgung (Stufe 3)',
  },
};

/**
 * Teilnahme (participation-state) tokens. The `short` forms name the *absence*
 * of emergency care explicitly ("Keine Notfallversorgung") because they are the
 * chip's fallback when no Notfallstufe is assigned — around two thirds of
 * clinics — and a bare "Keine Teilnahme" would not say what it is about.
 */
export const AYS_CLINIC_NOTFALL_PARTICIPATION_DISPLAY_NAMES: Record<
  string,
  AysClinicNotfallversorgungLabel
> = {
  keine_teilnahme: {
    short: 'Keine Notfallversorgung',
    full: 'Keine Teilnahme',
  },
  nichtteilnahme_noch_nicht_vereinbart: {
    short: 'Notfallstufe nicht vereinbart',
    full: 'Nichtteilnahme (noch nicht vereinbart)',
  },
  stufe_zugeordnet: {
    short: 'Notfallstufe zugeordnet',
    full: 'Stufe zugeordnet',
  },
};

/**
 * Fallback for an unknown `UPPER_SNAKE_CASE` token: lower-case it, then
 * capitalise each underscore-separated word (`STUFE_ZUGEORDNET` → "Stufe
 * Zugeordnet") so a token added to the backend enum still reads as words instead
 * of leaking a raw constant into the UI.
 */
function humanizeToken(token: string): string {
  return token
    .toLowerCase()
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ')
    .trim();
}

/** Resolve a token against `catalog`, humanizing anything it does not know. */
function resolve(
  catalog: Record<string, AysClinicNotfallversorgungLabel>,
  token: string | undefined | null,
): AysClinicNotfallversorgungLabel | undefined {
  const raw = token?.trim();

  if (!raw) {
    return undefined;
  }

  const humanized = humanizeToken(raw);

  return catalog[raw.toLowerCase()] ?? { short: humanized, full: humanized };
}

/** Resolve a raw Notfallstufe token to its display names. */
export function aysClinicNotfallLevelLabel(
  level: string | undefined | null,
): AysClinicNotfallversorgungLabel | undefined {
  return resolve(AYS_CLINIC_NOTFALL_LEVEL_DISPLAY_NAMES, level);
}

/** Resolve a raw Teilnahme (participation-state) token to its display names. */
export function aysClinicNotfallParticipationLabel(
  participationState: string | undefined | null,
): AysClinicNotfallversorgungLabel | undefined {
  return resolve(
    AYS_CLINIC_NOTFALL_PARTICIPATION_DISPLAY_NAMES,
    participationState,
  );
}

/**
 * The single Notfallversorgung label for a one-chip summary (clinic hero band):
 * the assigned Notfallstufe when there is one, otherwise the Teilnahme state,
 * which is what carries the information for the majority of clinics that have no
 * level. Undefined when the backend reports neither — the chip is then omitted
 * rather than shown empty, matching how the content section hides itself.
 */
export function aysClinicNotfallversorgungLabel(
  notfallversorgung: AysClinicNotfallversorgung | undefined | null,
): AysClinicNotfallversorgungLabel | undefined {
  return (
    aysClinicNotfallLevelLabel(notfallversorgung?.level) ??
    aysClinicNotfallParticipationLabel(notfallversorgung?.participationState)
  );
}
